import { SEEKING_TYPES } from '../constants/seeking';

const MAX_TOKEN_LENGTH = 80;
const MAX_SUMMARY_LENGTH = 280;
const MAX_DISPLAY_ITEMS = 5;

const SEEKING_TYPE_NONE = SEEKING_TYPES.NONE.toUpperCase();
const SEEKING_TYPE_BAND = 'BAND';
const SEEKING_TYPE_MEMBERS = 'MEMBERS';

const normalizeTextWhitespace = (value) => {
  if (typeof value !== 'string') {
    return '';
  }
  return value
    .replace(/[\r\n\t]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const sanitizeText = (value, maxLength = MAX_SUMMARY_LENGTH) => {
  return normalizeTextWhitespace(value).slice(0, maxLength);
};

const stripSeekingIntro = (value) => (
  sanitizeText(value)
    .replace(/^(currently\s+)?(looking\s+for|seeking|seeks|need|needs|searching\s+for)\s+/i, '')
    .trim()
);

const normalizeSeekingType = (value) => {
  if (typeof value !== 'string') {
    return '';
  }
  const normalized = value.trim().toUpperCase();
  if (normalized === SEEKING_TYPE_NONE) {
    return SEEKING_TYPE_NONE;
  }
  if (normalized === SEEKING_TYPE_BAND) {
    return SEEKING_TYPE_BAND;
  }
  if (normalized === SEEKING_TYPE_MEMBERS || normalized === 'MEMBER') {
    return SEEKING_TYPE_MEMBERS;
  }
  return '';
};

const normalizeStyleLabel = (value) => (
  sanitizeText(
    value?.style
    || value?.label
    || value?.name
    || '',
    MAX_TOKEN_LENGTH,
  )
);

const normalizeMemberLabel = (value) => (
  sanitizeText(
    value?.instrument
    || value?.label
    || value?.name
    || '',
    MAX_TOKEN_LENGTH,
  )
);

const normalizeLabels = (items, mapValue) => {
  if (!Array.isArray(items)) {
    return [];
  }
  const unique = new Set();
  items.forEach((item) => {
    const normalized = mapValue(item);
    if (normalized) {
      unique.add(normalized);
    }
  });
  return Array.from(unique).slice(0, MAX_DISPLAY_ITEMS);
};

const isExplicitNoneSeeking = (value) => (
  Boolean(value && typeof value === 'object' && !Array.isArray(value))
  && normalizeSeekingType(value.type) === SEEKING_TYPE_NONE
);

const resolveTranslation = (t, key, params, fallback) => {
  if (typeof t !== 'function') {
    return fallback;
  }
  const translated = t(key, {
    ...params,
    defaultValue: fallback,
  });
  return typeof translated === 'string' ? translated : fallback;
};

const normalizeSeekingObject = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }

  const type = normalizeSeekingType(value.type);
  if (type === SEEKING_TYPE_NONE) {
    return null;
  }

  return {
    summary: sanitizeText(value.summary || value.value || value.text || ''),
    type,
    bandStyles: normalizeLabels(value.bandStyles, normalizeStyleLabel),
    members: normalizeLabels(value.members, normalizeMemberLabel),
    notes: sanitizeText(value.notes || ''),
  };
};

export const parseSeekingPayload = (raw) => {
  if (raw == null) {
    return null;
  }

  if (typeof raw === 'object') {
    if (isExplicitNoneSeeking(raw)) {
      return null;
    }
    return normalizeSeekingObject(raw);
  }

  if (typeof raw !== 'string') {
    return null;
  }

  const normalizedRaw = normalizeTextWhitespace(raw);
  if (!normalizedRaw) {
    return null;
  }
  const fallbackSummary = normalizedRaw.slice(0, MAX_SUMMARY_LENGTH);

  if (!normalizedRaw.startsWith('{')) {
    return { summary: fallbackSummary, type: '', bandStyles: [], members: [], notes: '' };
  }

  try {
    const parsed = JSON.parse(normalizedRaw);
    if (isExplicitNoneSeeking(parsed)) {
      return null;
    }
    return normalizeSeekingObject(parsed) || { summary: fallbackSummary, type: '', bandStyles: [], members: [], notes: '' };
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn('Failed to parse seeking payload.', error);
    }
    return { summary: fallbackSummary, type: '', bandStyles: [], members: [], notes: '' };
  }
};

export const formatSeekingSummary = (raw, { t } = {}) => {
  const parsed = parseSeekingPayload(raw);
  if (!parsed) {
    return '';
  }

  if (parsed.summary) {
    return parsed.summary;
  }

  const stylesText = parsed.bandStyles.join(', ');
  const membersText = parsed.members.join(', ');

  if (parsed.type === SEEKING_TYPE_BAND && stylesText) {
    return resolveTranslation(
      t,
      'friendsPage.userList.profile.seekingBand',
      { styles: stylesText },
      `Looking for a band · ${stylesText}`,
    );
  }

  if (parsed.type === SEEKING_TYPE_MEMBERS && membersText) {
    return resolveTranslation(
      t,
      'friendsPage.userList.profile.seekingMembers',
      { instruments: membersText },
      `Looking for members: ${membersText}`,
    );
  }

  if (stylesText) {
    return resolveTranslation(
      t,
      'friendsPage.userList.profile.seekingBand',
      { styles: stylesText },
      `Looking for a band · ${stylesText}`,
    );
  }

  if (membersText) {
    return resolveTranslation(
      t,
      'friendsPage.userList.profile.seekingMembers',
      { instruments: membersText },
      `Looking for members: ${membersText}`,
    );
  }

  if (parsed.notes) {
    return parsed.notes;
  }

  if (parsed.type === SEEKING_TYPE_BAND) {
    return resolveTranslation(
      t,
      'friendsPage.userList.profile.seekingBandOnly',
      {},
      'Looking for a band',
    );
  }

  if (parsed.type === SEEKING_TYPE_MEMBERS) {
    return resolveTranslation(
      t,
      'friendsPage.userList.profile.seekingMembersOnly',
      {},
      'Looking for members',
    );
  }

  return '';
};

export const formatSeekingTargetPhrase = (raw) => {
  const parsed = parseSeekingPayload(raw);
  if (!parsed) {
    return '';
  }

  if (parsed.summary) {
    return stripSeekingIntro(parsed.summary);
  }

  const stylesText = parsed.bandStyles.join(', ');
  const membersText = parsed.members.join(', ');

  if (parsed.type === SEEKING_TYPE_MEMBERS && membersText) {
    return membersText;
  }

  if (parsed.type === SEEKING_TYPE_BAND && stylesText) {
    return `a band · ${stylesText}`;
  }

  if (membersText) {
    return membersText;
  }

  if (stylesText) {
    return `a band · ${stylesText}`;
  }

  if (parsed.notes) {
    return stripSeekingIntro(parsed.notes);
  }

  if (parsed.type === SEEKING_TYPE_BAND) {
    return 'a band';
  }

  if (parsed.type === SEEKING_TYPE_MEMBERS) {
    return 'members';
  }

  return '';
};
